import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Clock,
  PlayCircle,
  RefreshCw,
  Mail,
  Database,
  DollarSign,
  Calendar,
  Plus,
  Trash2,
  Pencil
} from 'lucide-react';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatCents } from '../utils/currencyFormat';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getFiscalYearStartYear = (year, month, fyStartMonth) => {
  let fy = year;
  if (month < fyStartMonth) fy -= 1;
  return fy;
};

const MONTH_OFFSET_OPTIONS = [
  { value: 0, label: 'Current month' },
  { value: 1, label: 'Previous month' },
  { value: 2, label: '2 months back' },
  { value: 3, label: '3 months back' },
  { value: 4, label: '4 months back' },
  { value: 5, label: '5 months back' },
  { value: 6, label: '6 months back' }
];

const DEFAULT_SCHEDULE_DRAFT = {
  name: '',
  enabled: false,
  schedule: '0 8 1 * *',
  timezone: 'America/New_York',
  monthOffset: 1,
  recipientEmails: '',
  subjectTemplate: 'Cursor Monthly Usage Report — {month} {year}'
};

const formatMonthOffsetLabel = (offset) => {
  const opt = MONTH_OFFSET_OPTIONS.find((o) => o.value === offset);
  return opt ? opt.label : `${offset} months back`;
};

const MonthlyUsageReportAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [imports, setImports] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [connectionTest, setConnectionTest] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const [testConnectionForm, setTestConnectionForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });

  const [schedulerForm, setSchedulerForm] = useState({
    schedule: '0 3 * * *',
    timezone: 'America/New_York',
    enabled: true
  });

  const [schedules, setSchedules] = useState([]);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleRunningId, setScheduleRunningId] = useState(null);

  const [fiscalYearForm, setFiscalYearForm] = useState({
    fiscalYearStartMonth: 1
  });

  const [prepaidFiscalYear, setPrepaidFiscalYear] = useState(null);
  const [prepaidOptions, setPrepaidOptions] = useState([]);
  const [prepaidGroups, setPrepaidGroups] = useState([]);
  const [prepaidDraft, setPrepaidDraft] = useState({});
  const [prepaidLoading, setPrepaidLoading] = useState(false);
  const [prepaidSaving, setPrepaidSaving] = useState(false);
  const [prepaidLoadError, setPrepaidLoadError] = useState(null);

  const [backfillForm, setBackfillForm] = useState({
    startYear: new Date().getFullYear() - 1,
    startMonth: 1,
    endYear: new Date().getFullYear(),
    endMonth: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
    forceRefresh: false
  });

  const [generateForm, setGenerateForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
    recipientEmails: ''
  });

  const [backfillBatchId, setBackfillBatchId] = useState(null);
  const [backfillProgress, setBackfillProgress] = useState([]);
  const pollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [statusRes, importsRes, schedulesRes, historyRes, settingsRes] = await Promise.all([
        api.get(endpoints.billingGroups.schedulerStatus).catch(() => ({ data: {} })),
        api.get(`${endpoints.billingGroups.imports}?limit=30`).catch(() => ({ data: { imports: [] } })),
        api.get(endpoints.monthlyUsageReport.schedules).catch(() => ({ data: { schedules: [] } })),
        api.get(`${endpoints.monthlyUsageReport.history}?limit=20`).catch(() => ({ data: { history: [] } })),
        api.get(endpoints.billingGroups.settings).catch(() => ({ data: {} }))
      ]);

      if (statusRes.data?.status) {
        setSchedulerStatus(statusRes.data.status);
        setSchedulerForm({
          schedule: statusRes.data.status.schedule || '0 3 * * *',
          timezone: statusRes.data.status.timezone || 'America/New_York',
          enabled: statusRes.data.status.enabled !== false
        });
      }

      setImports(importsRes.data?.imports || []);
      setSchedules(schedulesRes.data?.schedules || []);

      if (settingsRes.data?.fiscalYearStartMonth) {
        setFiscalYearForm({
          fiscalYearStartMonth: settingsRes.data.fiscalYearStartMonth
        });
      }

      setReportHistory(historyRes.data?.history || []);
    } catch (error) {
      console.error('Failed to load monthly usage admin data:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    setBackfillForm((f) => ({
      ...f,
      endYear: prevYear,
      endMonth: prevMonth,
      startYear: prevMonth === 12 ? prevYear : prevYear - 1,
      startMonth: prevMonth === 12 ? 1 : prevMonth + 1
    }));
    setGenerateForm((f) => ({ ...f, year: prevYear, month: prevMonth }));
    setTestConnectionForm((f) => ({ ...f, year: prevYear, month: prevMonth }));
  }, [loadData]);

  const pollBackfill = useCallback(async (batchId) => {
    try {
      const res = await api.get(`${endpoints.billingGroups.imports}?batchId=${batchId}&limit=50`);
      const rows = res.data?.imports || [];
      setBackfillProgress(rows);

      const pending = rows.filter((r) => r.Status === 'running' || r.Status === 'pending');
      if (pending.length === 0 && rows.length > 0) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        toast.success('Backfill batch completed');
        loadData();
      }
    } catch (e) {
      console.error('Backfill poll failed:', e);
    }
  }, [loadData]);

  const loadPrepaidData = useCallback(async (fiscalYearStartYear) => {
    if (!fiscalYearStartYear) return;
    setPrepaidLoading(true);
    setPrepaidLoadError(null);
    try {
      const res = await api.get(endpoints.billingGroups.prepaid, {
        params: { fiscalYearStartYear }
      });
      const groups = res.data?.groups || [];
      setPrepaidOptions(res.data?.fiscalYearOptions || []);
      setPrepaidGroups(groups);
      setPrepaidFiscalYear(fiscalYearStartYear);

      const draft = {};
      groups.forEach((g) => {
        draft[g.cursorGroupId] = g.isConfigured
          ? (parseFloat(g.committedPrepaidCents || 0) / 100).toFixed(2)
          : '';
      });
      setPrepaidDraft(draft);
    } catch (error) {
      console.error('Failed to load prepaid commitments:', error);
      const status = error.response?.status;
      const message = error.response?.data?.error || error.message || 'Unknown error';

      if (status === 404) {
        setPrepaidLoadError('The prepaid API is not available. Confirm v1.2.0+ is deployed on the server.');
      } else if (/BillingGroupPrepaidCommitment|Invalid object name/i.test(message)) {
        setPrepaidLoadError('Database migration required. Run Test Connection, then Run Migrations above.');
      } else {
        setPrepaidLoadError(message);
        toast.error('Failed to load committed prepaid values');
      }
    } finally {
      setPrepaidLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fiscalYearForm.fiscalYearStartMonth) return;
    const now = new Date();
    const fyStartYear = getFiscalYearStartYear(
      now.getFullYear(),
      now.getMonth() + 1,
      fiscalYearForm.fiscalYearStartMonth
    );
    loadPrepaidData(fyStartYear);
  }, [fiscalYearForm.fiscalYearStartMonth, loadPrepaidData]);

  const handlePrepaidSave = async () => {
    if (!prepaidFiscalYear) return;
    setPrepaidSaving(true);
    try {
      const commitments = prepaidGroups
        .filter((g) => {
          const val = prepaidDraft[g.cursorGroupId];
          return val !== '' && val != null && !Number.isNaN(parseFloat(val));
        })
        .map((g) => ({
          cursorGroupId: g.cursorGroupId,
          committedPrepaidCents: Math.round(parseFloat(prepaidDraft[g.cursorGroupId]) * 100)
        }));

      if (commitments.length === 0) {
        toast.error('Enter at least one committed prepaid amount to save');
        return;
      }

      await api.put(endpoints.billingGroups.prepaid, {
        fiscalYearStartYear: prepaidFiscalYear,
        commitments
      });
      toast.success('Committed prepaid values saved');
      loadPrepaidData(prepaidFiscalYear);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save committed prepaid values');
    } finally {
      setPrepaidSaving(false);
    }
  };

  useEffect(() => {
    if (backfillBatchId) {
      pollBackfill(backfillBatchId);
      pollRef.current = setInterval(() => pollBackfill(backfillBatchId), 3000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [backfillBatchId, pollBackfill]);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionTest(null);
    try {
      const res = await api.get(endpoints.billingGroups.testConnection, {
        params: {
          year: testConnectionForm.year,
          month: testConnectionForm.month
        }
      });
      setConnectionTest(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || 'Connection failed');
      }
    } catch (error) {
      const message = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Connection test failed';
      setConnectionTest({
        success: false,
        message: `Request failed: ${message}`
      });
      toast.error(message);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleMigrate = async () => {
    if (!connectionTest?.success) {
      toast.error('Run Test Connection successfully before running migrations.');
      return;
    }
    if (!window.confirm('Run billing group database migrations?')) return;
    try {
      await api.post(endpoints.billingGroups.migrate);
      toast.success('Migrations completed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Migration failed');
    }
  };

  const connectionVerified = connectionTest?.success === true;

  const handleSchedulerSave = async () => {
    try {
      await api.put(endpoints.billingGroups.schedulerConfig, schedulerForm);
      toast.success('Collection scheduler updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update scheduler');
    }
  };

  const handleSchedulerTrigger = async () => {
    try {
      await api.post(endpoints.billingGroups.schedulerTrigger);
      toast.success('Nightly refresh triggered (finalizes previous month + updates current month)');
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error('Failed to trigger import');
    }
  };

  const handleRefreshCurrentMonth = async () => {
    try {
      const res = await api.post(endpoints.billingGroups.refreshCurrentMonth);
      if (res.data.success) {
        toast.success('Current month refreshed from Cursor API');
      } else {
        toast.error(res.data.error || 'Refresh failed');
      }
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to refresh current month');
    }
  };

  const handleScheduleEdit = (schedule) => {
    setScheduleDraft({
      id: schedule.id,
      name: schedule.name,
      enabled: schedule.enabled,
      schedule: schedule.schedule,
      timezone: schedule.timezone,
      monthOffset: schedule.monthOffset ?? 1,
      recipientEmails: schedule.recipientEmails || '',
      subjectTemplate: schedule.subjectTemplate || DEFAULT_SCHEDULE_DRAFT.subjectTemplate
    });
  };

  const handleScheduleAdd = () => {
    setScheduleDraft({ ...DEFAULT_SCHEDULE_DRAFT });
  };

  const handleScheduleCancel = () => {
    setScheduleDraft(null);
  };

  const handleScheduleSave = async () => {
    if (!scheduleDraft) return;
    try {
      setScheduleSaving(true);
      const payload = {
        name: scheduleDraft.name,
        enabled: scheduleDraft.enabled,
        schedule: scheduleDraft.schedule,
        timezone: scheduleDraft.timezone,
        monthOffset: scheduleDraft.monthOffset,
        recipientEmails: scheduleDraft.recipientEmails,
        subjectTemplate: scheduleDraft.subjectTemplate
      };

      if (scheduleDraft.id) {
        await api.put(endpoints.monthlyUsageReport.schedule(scheduleDraft.id), payload);
        toast.success('Schedule updated');
      } else {
        await api.post(endpoints.monthlyUsageReport.schedules, payload);
        toast.success('Schedule created');
      }

      setScheduleDraft(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleScheduleDelete = async (id) => {
    if (!window.confirm('Delete this email schedule?')) return;
    try {
      await api.delete(endpoints.monthlyUsageReport.schedule(id));
      toast.success('Schedule deleted');
      if (scheduleDraft?.id === id) setScheduleDraft(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete schedule');
    }
  };

  const handleScheduleRunNow = async (id) => {
    try {
      setScheduleRunningId(id);
      const res = await api.post(endpoints.monthlyUsageReport.runSchedule(id));
      if (res.data.success) {
        toast.success(`Report sent to ${res.data.successCount} recipient(s)`);
      } else {
        toast.error(res.data.error || 'Report send had failures');
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to run schedule');
    } finally {
      setScheduleRunningId(null);
    }
  };

  const handleFiscalYearSave = async () => {
    try {
      await api.put(endpoints.billingGroups.settings, fiscalYearForm);
      toast.success('Fiscal year settings saved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save fiscal year settings');
    }
  };

  const handleBackfill = async () => {
    try {
      const res = await api.post(endpoints.billingGroups.backfill, backfillForm);
      if (res.data.success) {
        setBackfillBatchId(res.data.batchId);
        toast.success(`Backfill started (${res.data.totalMonths} months)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start backfill');
    }
  };

  const handleGenerateReport = async () => {
    try {
      if (!generateForm.recipientEmails.trim()) {
        toast.error('Recipients are required for one-time reports');
        return;
      }
      const payload = {
        year: generateForm.year,
        month: generateForm.month,
        recipientEmails: generateForm.recipientEmails
      };
      const res = await api.post(endpoints.monthlyUsageReport.generate, payload);
      if (res.data.success) {
        toast.success(`Report sent to ${res.data.successCount} recipient(s)`);
      } else {
        toast.error(res.data.error || 'Report send had failures');
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate report');
    }
  };

  const statusBadge = (status) => {
    const map = {
      completed: 'badge-success',
      failed: 'badge-danger',
      skipped: 'badge-secondary',
      running: 'badge-warning',
      pending: 'badge-warning',
      partial: 'badge-warning'
    };
    return map[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-secondary-900">Monthly Usage Reports</h2>
          <p className="text-secondary-600 mt-1">
            Billing group data collection, backfill, and email reports
          </p>
        </div>
        <button onClick={loadData} className="btn btn-outline flex items-center">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* API Connection */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="font-semibold flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Cursor Billing Groups API
          </h3>
          <div className="flex gap-2">
            <button
              onClick={handleMigrate}
              disabled={!connectionVerified}
              className="btn btn-outline btn-sm"
              title={connectionVerified ? 'Create billing group tables' : 'Test connection successfully first'}
            >
              Run Migrations
            </button>
          </div>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-secondary-600">
            Step 1: Test Connection verifies your API key and fetches billing group spend for a specific month.
            Step 2: Run Migrations only after the connection test succeeds.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
            <div>
              <label className="form-label">Test Year</label>
              <input
                type="number"
                className="input w-full"
                value={testConnectionForm.year}
                onChange={(e) => setTestConnectionForm({
                  ...testConnectionForm,
                  year: parseInt(e.target.value, 10)
                })}
              />
            </div>
            <div>
              <label className="form-label">Test Month</label>
              <select
                className="input w-full"
                value={testConnectionForm.month}
                onChange={(e) => setTestConnectionForm({
                  ...testConnectionForm,
                  month: parseInt(e.target.value, 10)
                })}
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className="btn btn-primary w-full"
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>
          <p className="text-xs text-secondary-500">
            Current month uses the live billing cycle. Historical months use that month&apos;s billing cycle date.
          </p>
          {connectionTest && (
            <div className={`rounded-lg border p-4 text-sm ${connectionTest.success ? 'border-success-200 bg-success-50 text-success-900' : 'border-danger-200 bg-danger-50 text-danger-900'}`}>
              <p className="font-medium">{connectionTest.message}</p>

              {connectionTest.testedPeriod && (
                <p className="mt-2">Tested period: <strong>{connectionTest.testedPeriod}</strong></p>
              )}

              {connectionTest.billingCycle && (
                <p className="mt-1">API billing cycle: {connectionTest.billingCycle}</p>
              )}

              {connectionTest.apiKeyValid && connectionTest.memberCount != null && (
                <p className="mt-2">Team members accessible: {connectionTest.memberCount}</p>
              )}

              {connectionTest.teamMembersSample?.length > 0 && (
                <div className="mt-3">
                  <p className="font-medium mb-1">Sample team members</p>
                  <ul className="list-disc list-inside space-y-1">
                    {connectionTest.teamMembersSample.map((member) => (
                      <li key={`${member.email}-${member.role}`}>
                        {member.name} ({member.email}) — {member.role}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {connectionTest.success && connectionTest.groups?.length > 0 && (
                <div className="mt-3 overflow-x-auto">
                  <p className="font-medium mb-2">
                    Billing groups ({connectionTest.groupCount})
                  </p>
                  <table className="table text-sm bg-white">
                    <thead>
                      <tr>
                        <th>Group</th>
                        <th className="text-right">Members</th>
                        <th className="text-right">Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectionTest.groups.map((group) => (
                        <tr key={group.id || group.name}>
                          <td>{group.name}</td>
                          <td className="text-right">{group.memberCount}</td>
                          <td className="text-right">{formatCents(group.spendCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold border-t-2">
                        <td>Total</td>
                        <td className="text-right">{connectionTest.totalMemberCount ?? '—'}</td>
                        <td className="text-right">{formatCents(connectionTest.totalSpendCents)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {connectionTest.success && connectionTest.groupCount === 0 && (
                <p className="mt-2">No billing groups returned for this period.</p>
              )}

              {!connectionTest.success && connectionTest.status && (
                <p className="mt-2">HTTP status: {connectionTest.status}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data Collection */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            Data Collection (Nightly)
          </h3>
        </div>
        <div className="card-body space-y-4">
          {schedulerStatus && (
            <div className="flex flex-wrap gap-4 text-sm text-secondary-600">
              <span>Status: {schedulerStatus.scheduled ? 'Scheduled' : 'Stopped'}</span>
              {schedulerStatus.nextRunFormatted && (
                <span>Next run: {schedulerStatus.nextRunFormatted}</span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Cron Schedule</label>
              <input
                className="input w-full"
                value={schedulerForm.schedule}
                onChange={(e) => setSchedulerForm({ ...schedulerForm, schedule: e.target.value })}
                placeholder="0 3 * * *"
              />
              <p className="text-xs text-secondary-500 mt-1">Default: 0 3 * * * (3am daily)</p>
            </div>
            <div>
              <label className="form-label">Timezone</label>
              <input
                className="input w-full"
                value={schedulerForm.timezone}
                onChange={(e) => setSchedulerForm({ ...schedulerForm, timezone: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={schedulerForm.enabled}
                  onChange={(e) => setSchedulerForm({ ...schedulerForm, enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleSchedulerSave} className="btn btn-primary">Save Scheduler</button>
            <button onClick={handleRefreshCurrentMonth} className="btn btn-secondary flex items-center">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Current Month
            </button>
            <button onClick={handleSchedulerTrigger} className="btn btn-outline flex items-center">
              <PlayCircle className="w-4 h-4 mr-2" />
              Run Full Nightly Job
            </button>
          </div>
          <p className="text-xs text-secondary-500">
            Use <strong>Refresh Current Month</strong> for month-to-date spend (overwrites prior import).
            Use <strong>Run Full Nightly Job</strong> to also finalize the previous month if needed.
          </p>

          <div className="overflow-x-auto mt-4">
            <h4 className="font-medium mb-2">Import History</h4>
            <table className="table text-sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Records</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {imports.slice(0, 15).map((row) => (
                  <tr key={row.ImportId}>
                    <td>{row.ImportType}</td>
                    <td>{row.Year}-{String(row.Month).padStart(2, '0')}</td>
                    <td><span className={`badge ${statusBadge(row.Status)}`}>{row.Status}</span></td>
                    <td>{row.ProcessedRecords}/{row.TotalRecords}</td>
                    <td>{row.StartedAt ? new Date(row.StartedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Backfill */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold flex items-center">
            <Database className="w-5 h-5 mr-2" />
            Backfill Previous Months
          </h3>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-secondary-600">
            One-time import of <strong>completed</strong> historical months. For the current in-progress month,
            use <strong>Refresh Current Month</strong> in Data Collection above.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Start Year</label>
              <input type="number" className="input w-full" value={backfillForm.startYear}
                onChange={(e) => setBackfillForm({ ...backfillForm, startYear: parseInt(e.target.value, 10) })} />
            </div>
            <div>
              <label className="form-label">Start Month</label>
              <select className="input w-full" value={backfillForm.startMonth}
                onChange={(e) => setBackfillForm({ ...backfillForm, startMonth: parseInt(e.target.value, 10) })}>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">End Year</label>
              <input type="number" className="input w-full" value={backfillForm.endYear}
                onChange={(e) => setBackfillForm({ ...backfillForm, endYear: parseInt(e.target.value, 10) })} />
            </div>
            <div>
              <label className="form-label">End Month</label>
              <select className="input w-full" value={backfillForm.endMonth}
                onChange={(e) => setBackfillForm({ ...backfillForm, endMonth: parseInt(e.target.value, 10) })}>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={backfillForm.forceRefresh}
              onChange={(e) => setBackfillForm({ ...backfillForm, forceRefresh: e.target.checked })}
            />
            Force refresh already-collected months
          </label>
          <button onClick={handleBackfill} className="btn btn-primary">Start Backfill</button>

          {backfillProgress.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Backfill Progress</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {backfillProgress.map((row) => (
                  <div key={row.ImportId} className="flex justify-between text-sm py-1 border-b border-secondary-100">
                    <span>{row.Year}-{String(row.Month).padStart(2, '0')}</span>
                    <span className={`badge ${statusBadge(row.Status)}`}>{row.Status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fiscal Year */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Fiscal Year
          </h3>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-secondary-600">
            Defines the fiscal year used for YTD totals on the Monthly Usage page, email reports, and all billing group reporting.
          </p>
          <div className="max-w-md">
            <label className="form-label">Fiscal Year Start Month</label>
            <select
              className="input w-full"
              value={fiscalYearForm.fiscalYearStartMonth}
              onChange={(e) => setFiscalYearForm({
                ...fiscalYearForm,
                fiscalYearStartMonth: parseInt(e.target.value, 10)
              })}
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i + 1}>{name}</option>
              ))}
            </select>
            <p className="text-xs text-secondary-500 mt-1">
              Example: July for a July–June fiscal year. January uses the calendar year.
            </p>
          </div>
          <button onClick={handleFiscalYearSave} className="btn btn-primary">
            Save Fiscal Year
          </button>
        </div>
      </div>

      {/* Committed Prepaid Allocation */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Committed Prepaid Allocation
          </h3>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-secondary-600">
            Set the annual committed prepaid amount per billing group for each fiscal year.
            When a new fiscal year starts, fields are blank until you enter new values — prior years are kept for reporting.
            The Unassigned group is excluded.
          </p>

          {prepaidLoadError && (
            <div className="p-3 rounded-md bg-error-50 text-error-800 text-sm border border-error-200">
              {prepaidLoadError}
            </div>
          )}

          <div className="max-w-md">
            <label className="form-label">Fiscal Year</label>
            <select
              className="input w-full"
              value={prepaidFiscalYear || ''}
              onChange={(e) => loadPrepaidData(parseInt(e.target.value, 10))}
              disabled={prepaidLoading || prepaidOptions.length === 0}
            >
              {prepaidOptions.map((opt) => (
                <option key={opt.fiscalYearStartYear} value={opt.fiscalYearStartYear}>
                  {opt.fiscalYearLabel}
                  {opt.hasConfiguredValues ? ' (configured)' : ''}
                </option>
              ))}
            </select>
          </div>

          {prepaidLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : prepaidGroups.length === 0 ? (
            <p className="text-sm text-secondary-500">
              No billing groups found. Run data collection or test connection first.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table text-sm">
                  <thead>
                    <tr>
                      <th>Billing Group</th>
                      <th className="text-right">Committed Prepaid ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepaidGroups.map((group) => (
                      <tr key={group.cursorGroupId}>
                        <td className="font-medium">{group.groupName}</td>
                        <td className="text-right">
                          <input
                            type="number"
                            className="input w-40 text-right ml-auto"
                            min="0"
                            step="0.01"
                            placeholder="Not set"
                            value={prepaidDraft[group.cursorGroupId] ?? ''}
                            onChange={(e) => setPrepaidDraft({
                              ...prepaidDraft,
                              [group.cursorGroupId]: e.target.value
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={handlePrepaidSave}
                disabled={prepaidSaving}
                className="btn btn-primary"
              >
                {prepaidSaving ? 'Saving...' : 'Save All'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Scheduled Email Reports */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="font-semibold flex items-center">
            <Mail className="w-5 h-5 mr-2" />
            Scheduled Email Reports
          </h3>
          <button
            type="button"
            onClick={handleScheduleAdd}
            className="btn btn-outline btn-sm flex items-center"
            disabled={!!scheduleDraft && !scheduleDraft.id}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Schedule
          </button>
        </div>
        <div className="card-body space-y-4">
          <p className="text-sm text-secondary-600">
            Create multiple named schedules with different recipients, frequencies, and reporting periods.
            Use <strong>Current month</strong> for daily/weekly updates; use <strong>Previous month</strong> for monthly close-out reports.
          </p>

          {scheduleDraft && (
            <div className="border border-primary-200 rounded-lg p-4 bg-primary-50/30 space-y-4">
              <h4 className="font-medium text-secondary-900">
                {scheduleDraft.id ? 'Edit Schedule' : 'New Schedule'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Name</label>
                  <input
                    className="input w-full"
                    value={scheduleDraft.name}
                    onChange={(e) => setScheduleDraft({ ...scheduleDraft, name: e.target.value })}
                    placeholder="e.g. Finance Daily, Exec Monthly"
                  />
                </div>
                <div>
                  <label className="form-label">Report Period</label>
                  <select
                    className="input w-full"
                    value={scheduleDraft.monthOffset}
                    onChange={(e) => setScheduleDraft({ ...scheduleDraft, monthOffset: parseInt(e.target.value, 10) })}
                  >
                    {MONTH_OFFSET_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Cron Schedule</label>
                  <input
                    className="input w-full"
                    value={scheduleDraft.schedule}
                    onChange={(e) => setScheduleDraft({ ...scheduleDraft, schedule: e.target.value })}
                  />
                  <p className="text-xs text-secondary-500 mt-1">Example: 0 8 1 * * (8am on 1st of month)</p>
                </div>
                <div>
                  <label className="form-label">Timezone</label>
                  <input
                    className="input w-full"
                    value={scheduleDraft.timezone}
                    onChange={(e) => setScheduleDraft({ ...scheduleDraft, timezone: e.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={scheduleDraft.enabled}
                      onChange={(e) => setScheduleDraft({ ...scheduleDraft, enabled: e.target.checked })}
                    />
                    Enable this schedule
                  </label>
                </div>
              </div>
              <div>
                <label className="form-label">Recipients (comma-separated)</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={scheduleDraft.recipientEmails}
                  onChange={(e) => setScheduleDraft({ ...scheduleDraft, recipientEmails: e.target.value })}
                  placeholder="finance@company.com, admin@company.com"
                />
              </div>
              <div>
                <label className="form-label">Email Subject Template</label>
                <input
                  className="input w-full"
                  value={scheduleDraft.subjectTemplate}
                  onChange={(e) => setScheduleDraft({ ...scheduleDraft, subjectTemplate: e.target.value })}
                  placeholder="Use {month} and {year} placeholders"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleScheduleSave}
                  disabled={scheduleSaving}
                  className="btn btn-primary"
                >
                  {scheduleSaving ? 'Saving...' : (scheduleDraft.id ? 'Save Changes' : 'Create Schedule')}
                </button>
                <button type="button" onClick={handleScheduleCancel} className="btn btn-outline">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {schedules.length === 0 && !scheduleDraft && (
            <p className="text-secondary-500 text-sm">No schedules configured. Click &quot;Add Schedule&quot; to create one.</p>
          )}

          <div className="space-y-3">
            {schedules.map((s) => (
              <div key={s.id} className="border border-secondary-200 rounded-lg p-4">
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-secondary-900">{s.name}</h4>
                      <span className={`badge ${s.enabled ? 'badge-success' : 'badge-secondary'}`}>
                        {s.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {s.scheduler?.running && (
                        <span className="badge badge-warning">Running</span>
                      )}
                    </div>
                    <p className="text-sm text-secondary-600 mt-1">
                      Reports on: <strong>{formatMonthOffsetLabel(s.monthOffset)}</strong>
                      {' · '}
                      Cron: <code className="text-xs">{s.schedule}</code>
                      {' · '}
                      {s.timezone}
                    </p>
                    {s.scheduler?.nextRunFormatted && (
                      <p className="text-xs text-secondary-500 mt-1">Next run: {s.scheduler.nextRunFormatted}</p>
                    )}
                    <p className="text-xs text-secondary-500 mt-1 truncate max-w-xl" title={s.recipientEmails}>
                      Recipients: {s.recipientEmails}
                    </p>
                    {s.lastRunAt && (
                      <p className="text-xs text-secondary-500 mt-1">
                        Last run: {new Date(s.lastRunAt).toLocaleString()}
                        {s.lastRunStatus ? ` (${s.lastRunStatus})` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleScheduleRunNow(s.id)}
                      disabled={scheduleRunningId === s.id}
                      className="btn btn-outline btn-sm flex items-center"
                    >
                      <PlayCircle className="w-4 h-4 mr-1" />
                      {scheduleRunningId === s.id ? 'Sending...' : 'Run Now'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScheduleEdit(s)}
                      className="btn btn-outline btn-sm flex items-center"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScheduleDelete(s.id)}
                      className="btn btn-outline btn-sm flex items-center text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* One-Time Report */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Generate One-Time Report
          </h3>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Year</label>
              <input type="number" className="input w-full" value={generateForm.year}
                onChange={(e) => setGenerateForm({ ...generateForm, year: parseInt(e.target.value, 10) })} />
            </div>
            <div>
              <label className="form-label">Month</label>
              <select className="input w-full" value={generateForm.month}
                onChange={(e) => setGenerateForm({ ...generateForm, month: parseInt(e.target.value, 10) })}>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Recipients (required)</label>
            <textarea className="input w-full" rows={2} value={generateForm.recipientEmails}
              onChange={(e) => setGenerateForm({ ...generateForm, recipientEmails: e.target.value })}
              placeholder="finance@company.com, admin@company.com" />
          </div>
          <button onClick={handleGenerateReport} className="btn btn-primary flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Generate &amp; Send
          </button>
        </div>
      </div>

      {/* Report History */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold">Report Send History</h3>
        </div>
        <div className="card-body overflow-x-auto">
          <table className="table text-sm">
            <thead>
              <tr>
                <th>Run</th>
                <th>Schedule</th>
                <th>Period</th>
                <th>Fiscal Year</th>
                <th>Recipients</th>
                <th>Status</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {reportHistory.map((row) => (
                <tr key={row.Id}>
                  <td>{row.RunType}</td>
                  <td>{row.ScheduleName || (row.RunType === 'manual' ? 'Manual' : '—')}</td>
                  <td>{row.Year}-{String(row.Month).padStart(2, '0')}</td>
                  <td>{row.FiscalYearLabel || '—'}</td>
                  <td className="max-w-xs truncate">{row.RecipientEmails}</td>
                  <td><span className={`badge ${statusBadge(row.Status)}`}>{row.Status}</span></td>
                  <td>{row.SentAt ? new Date(row.SentAt).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {reportHistory.length === 0 && (
                <tr><td colSpan="7" className="text-center text-secondary-500">No reports sent yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlyUsageReportAdmin;
